import * as React from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { useAuth } from '@/features/auth/contexto';
import { esquemaSenha } from '@/features/auth/senha';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { CriteriosSenha } from '@/components/criterios-senha';
import { Cartao, CartaoCabecalho, CartaoConteudo, CartaoTitulo } from '@/components/ui/card';

const esquema = z
  .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual.'),
    novaSenha: esquemaSenha,
    confirmacao: z.string().min(1, 'Repita a nova senha.'),
  })
  .refine((dados) => dados.novaSenha === dados.confirmacao, {
    message: 'As senhas não conferem.',
    path: ['confirmacao'],
  })
  .refine((dados) => dados.novaSenha !== dados.senhaAtual, {
    message: 'A nova senha deve ser diferente da atual.',
    path: ['novaSenha'],
  });

type Formulario = z.infer<typeof esquema>;

export function PaginaTrocarSenha() {
  const { usuario, sair } = useAuth();
  const navegar = useNavigate();
  const [erro, setErro] = React.useState<string | null>(null);

  const obrigatoria = usuario?.precisaTrocarSenha === true;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { senhaAtual: '', novaSenha: '', confirmacao: '' },
  });

  const senhaDigitada = watch('novaSenha');

  const aoEnviar = handleSubmit(async (dados) => {
    setErro(null);

    try {
      await api.post('/auth/change-password', {
        senhaAtual: dados.senhaAtual,
        novaSenha: dados.novaSenha,
      });

      // O backend revoga todas as sessões ao trocar a senha, então a sessão
      // atual também morre: o caminho correto é reautenticar.
      toast.success('Senha alterada. Entre novamente com a nova senha.');
      await sair();
      void navegar('/login', { replace: true });
    } catch (e) {
      setErro(
        e instanceof ErroApi ? e.mensagemUsuario : 'Não foi possível concluir. Tente novamente.',
      );
    }
  });

  const conteudo = (
    <form onSubmit={(evento) => void aoEnviar(evento)} noValidate className="flex flex-col gap-4">
      {obrigatoria && (
        <div className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto flex items-start gap-2 rounded-md border p-3 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            Sua senha é provisória. Defina uma senha pessoal para continuar usando o sistema.
          </span>
        </div>
      )}

      {erro && (
        <div
          role="alert"
          className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{erro}</span>
        </div>
      )}

      <Campo id="senhaAtual" rotulo="Senha atual" obrigatorio erro={errors.senhaAtual?.message}>
        <Entrada
          type="password"
          autoComplete="current-password"
          autoFocus
          {...register('senhaAtual')}
        />
      </Campo>

      <Campo id="novaSenha" rotulo="Nova senha" obrigatorio erro={errors.novaSenha?.message}>
        <Entrada type="password" autoComplete="new-password" {...register('novaSenha')} />
      </Campo>

      <CriteriosSenha senha={senhaDigitada} />

      <Campo
        id="confirmacao"
        rotulo="Repita a nova senha"
        obrigatorio
        erro={errors.confirmacao?.message}
      >
        <Entrada type="password" autoComplete="new-password" {...register('confirmacao')} />
      </Campo>

      <p className="text-silenciado-texto text-xs">
        Ao trocar a senha, todas as suas sessões abertas são encerradas — inclusive esta.
      </p>

      <Botao type="submit" disabled={isSubmitting}>
        {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
        {isSubmitting ? 'Salvando…' : 'Salvar nova senha'}
      </Botao>
    </form>
  );

  // Na troca obrigatória, a página ocupa a tela inteira: o menu lateral não
  // deve sugerir que há outros lugares para ir antes de resolver isto.
  if (obrigatoria) {
    return (
      <main className="bg-fundo flex min-h-dvh items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <span className="bg-marca text-marca-contraste flex size-14 items-center justify-center rounded-2xl">
              <KeyRound className="size-7" aria-hidden="true" />
            </span>
            <h1 className="text-xl font-semibold tracking-tight">Defina sua senha</h1>
          </div>
          <div className="bg-cartao rounded-xl border p-6 shadow-sm">{conteudo}</div>
        </div>
      </main>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo>Trocar senha</CartaoTitulo>
        </CartaoCabecalho>
        <CartaoConteudo>{conteudo}</CartaoConteudo>
      </Cartao>
    </div>
  );
}
